"use client";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import SocialIcons from "./SocialIcons";
import Logo from "./Logo";
import ParticleBackground from "./ParticleBackground";

export default function Footer({ year }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set this to true when you want to display social icons
  const showSocialIcons = false;
  const pathname = usePathname();
  const isDiscoveryPage = pathname === "/discovery";

  // Use static year to prevent hydration mismatch
  const currentYear = year || 2025;

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch("/api/services");
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      } else {
        console.warn("Failed to fetch services from API");
      }
    } catch (error) {
      console.warn("Error fetching services from API:", error);
    } finally {
      setLoading(false);
    }
  };

  // Group services by displayCategory
  const groupedServices = services.reduce((acc, service) => {
    const category = service.displayCategory || "Other Services";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(service);
    return acc;
  }, {});

  // Define the order of categories for footer
  const categoryOrder = [
    "Website Packages",
    "Ongoing Support",
    "Other Services",
  ];

  return (
    <footer className="bg-[var(--bg-primary)] text-[var(--text-secondary)] relative overflow-hidden">
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--brand-color)]/30 to-transparent"></div>

      {/* Particle background */}
      <ParticleBackground density={10} opacity={0.02} />

      <div className="max-w-6xl mx-auto px-6 py-12">
        {!isDiscoveryPage ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Business Info - 4 columns */}
              <div className="flex flex-col md:col-span-4">
                <Logo />
                <p className="mt-4 text-sm">
                  I'm Arthur Behesnilian, a web designer building custom
                  websites for businesses and service brands. Hand-coded,
                  fast, and built to last.
                </p>
                <div className="mt-4 text-sm text-[var(--text-secondary)]">
                  <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--brand-color)] mb-2">
                    Service Areas
                  </p>
                  <p>
                    Based in <strong>Pasadena</strong>, working with founders,
                    consultants, and service businesses across{" "}
                    <strong>Los Angeles</strong>,{" "}
                    <strong>Southern California</strong>, and nationwide.
                  </p>
                </div>
                {/* Social icons can be easily re-enabled by changing the showSocialIcons flag */}
                {showSocialIcons && (
                  <div className="mt-6">
                    <SocialIcons />
                  </div>
                )}
              </div>

              {/* Gap - 1 column */}
              <div className="hidden md:block md:col-span-1"></div>

              {/* Navigation - 2 columns */}
              <div className="space-y-4 md:col-span-2 mt-8 md:mt-0">
                <h3 className="font-mono text-xs font-medium text-[var(--brand-color)] uppercase tracking-[0.18em]">
                  Navigation
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a
                      href="/"
                      className="hover:text-[var(--brand-color)] transition"
                    >
                      Home
                    </a>
                  </li>
                  <li>
                    <a
                      href="/about"
                      className="hover:text-[var(--brand-color)] transition"
                    >
                      About
                    </a>
                  </li>
                  <li>
                    <a
                      href="/services"
                      className="hover:text-[var(--brand-color)] transition"
                    >
                      Services
                    </a>
                  </li>
                  <li>
                    <a
                      href="/portfolio"
                      className="hover:text-[var(--brand-color)] transition"
                    >
                      Portfolio
                    </a>
                  </li>
                  <li>
                    <a
                      href="/contact"
                      className="hover:text-[var(--brand-color)] transition"
                    >
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Services - 3 columns (more space for content) */}
              <div className="space-y-4 md:col-span-3 mt-8 md:mt-0">
                <h3 className="font-mono text-xs font-medium text-[var(--brand-color)] uppercase tracking-[0.18em]">
                  Services
                </h3>
                {loading ? (
                  <div className="text-sm">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {categoryOrder.map((categoryName) => {
                      const categoryServices = groupedServices[categoryName];
                      if (!categoryServices || categoryServices.length === 0)
                        return null;

                      return (
                        <div key={categoryName}>
                          <h4 className="text-xs font-medium text-[var(--text-primary)] mb-2 uppercase tracking-wider">
                            {categoryName}
                          </h4>
                          <ul className="space-y-1 text-sm ml-2">
                            {categoryServices.map((service) => (
                              <li key={service.id}>
                                <a
                                  href="#services"
                                  className="hover:text-[var(--brand-color)] transition text-[var(--text-secondary)]"
                                  title={service.description}
                                >
                                  {service.title}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Legal - 2 columns */}
              <div className="space-y-4 md:col-span-2 mt-8 md:mt-0">
                <h3 className="font-mono text-xs font-medium text-[var(--brand-color)] uppercase tracking-[0.18em]">
                  Legal
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a
                      href="/privacy-policy"
                      className="hover:text-[var(--brand-color)] transition"
                    >
                      Privacy Policy
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-12 pt-6 border-t border-[var(--border-color)] text-center">
              <p className="text-xs">
                behes.dev © {currentYear} All rights reserved.
              </p>
            </div>
          </>
        ) : (
          // Simplified footer for discovery page
          <div className="text-center">
            <p className="text-xs mb-2">
              behes.dev © {currentYear} All rights reserved.
            </p>
            <div className="text-xs">
              <a
                href="/privacy-policy"
                className="hover:text-[var(--brand-color)] transition"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}
